// ─────────────────────────────────────────────────────────────────────────
// WebMCP tool registration.
//
// Per the WebMCP spec, a page exposes tools to an in-browser AI agent via
// `window.document.modelContext.registerTool(...)`. Each tool here is a thin
// wrapper over a store action, so the agent drives the SAME code the human UI
// does. If the browser doesn't support WebMCP, registration no-ops and the app
// keeps working as an ordinary web app.
//
// Testable in Chrome with chrome://flags/#enable-webmcp-testing, or inside an
// agent host such as ChatGPT's in-app browser.
// ─────────────────────────────────────────────────────────────────────────
import { actions, getState } from "./store";

// ── Minimal WebMCP typings (spec is still evolving; keep them local) ────────
interface ToolResultContent {
  type: "text";
  text: string;
}
interface ToolResult {
  content: ToolResultContent[];
  isError?: boolean;
}
interface ToolDescriptor {
  name: string;
  description: string;
  /** JSON Schema for the tool's arguments. */
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<ToolResult> | ToolResult;
}
interface ModelContext {
  registerTool: (tool: ToolDescriptor) => void | (() => void);
}

function getModelContext(): ModelContext | null {
  if (typeof document === "undefined") return null;
  const mc = (document as unknown as { modelContext?: ModelContext })
    .modelContext;
  return mc && typeof mc.registerTool === "function" ? mc : null;
}

/** Whether the WebMCP API is present in this browser/host. */
export function isWebMcpAvailable(): boolean {
  return getModelContext() !== null;
}

/** Names of the tools this workspace registers — shown in the UI. */
export const REGISTERED_TOOL_NAMES = [
  "import_from_naukri",
  "discover_company_jobs",
  "add_job_to_board",
  "match_with_resume",
  "list_board_jobs",
] as const;

function ok(text: string): ToolResult {
  return { content: [{ type: "text", text }] };
}
function fail(text: string): ToolResult {
  return { content: [{ type: "text", text }], isError: true };
}

const str = (v: unknown): string => (typeof v === "string" ? v : String(v ?? ""));

/**
 * Register all workspace tools. Returns true if WebMCP was available.
 * Safe to call once on app startup.
 */
export function registerWebMcpTools(): boolean {
  const mc = getModelContext();
  if (!mc) {
    actions.noteSystem(
      "WebMCP not detected — running as a standard web app. Enable " +
        "chrome://flags/#enable-webmcp-testing or open in an agent browser.",
      "ok",
    );
    return false;
  }

  // Tool 0 — the FRONT of the pipeline: turn Naukri listings into official jobs.
  mc.registerTool({
    name: "import_from_naukri",
    description:
      "Import jobs starting FROM a Naukri (or similar aggregator) results page. " +
      "Naukri shows a company NAME but not its official posting URL, so this " +
      "harvests the companies and, for EACH, discovers the company's OFFICIAL " +
      "career portal and loads all matching openings (the portal usually lists " +
      "far more roles than the aggregator). When you (the agent) are on a Naukri " +
      "results page, read the visible listings and pass them as `listings`; " +
      "otherwise omit `listings` and the server will try to read Naukri itself.",
    inputSchema: {
      type: "object",
      properties: {
        roleQuery: {
          type: "string",
          description: "Role focus searched on Naukri, e.g. 'AI Engineer'",
        },
        location: {
          type: "string",
          description: "Optional city filter, e.g. 'Bengaluru'",
        },
        listings: {
          type: "array",
          description:
            "Listings scraped from the live Naukri page. Omit to have the " +
            "server fetch Naukri directly (may be blocked).",
          items: {
            type: "object",
            properties: {
              company: { type: "string" },
              title: { type: "string" },
              location: { type: "string" },
            },
            required: ["company", "title"],
          },
        },
      },
      required: ["roleQuery"],
    },
    async execute(args) {
      try {
        const raw = Array.isArray(args.listings) ? args.listings : [];
        const listings = raw
          .map((l) => (l && typeof l === "object" ? (l as Record<string, unknown>) : {}))
          .map((l) => ({
            company: str(l.company),
            title: str(l.title),
            location: l.location ? str(l.location) : undefined,
          }))
          .filter((l) => l.company && l.title);
        const res = await actions.importFromNaukri(
          {
            roleQuery: str(args.roleQuery),
            location: args.location ? str(args.location) : undefined,
            listings: listings.length ? listings : undefined,
          },
          "agent",
        );
        return ok(res.note);
      } catch (err) {
        return fail(err instanceof Error ? err.message : "Naukri import failed.");
      }
    },
  });

  // Tool 1 — discover a company's official openings and load them on the board.
  mc.registerTool({
    name: "discover_company_jobs",
    description:
      "Discover a company's OFFICIAL career-portal job openings (bypassing " +
      "aggregators like Naukri/Indeed) and add them to the pipeline board. " +
      "Use when the user names a company and a role focus.",
    inputSchema: {
      type: "object",
      properties: {
        companyName: { type: "string", description: "e.g. 'HP' or 'Stripe'" },
        roleQuery: {
          type: "string",
          description: "Role focus, e.g. 'AI Engineer'",
        },
      },
      required: ["companyName", "roleQuery"],
    },
    async execute(args) {
      try {
        const res = await actions.discover(
          { companyName: str(args.companyName), roleQuery: str(args.roleQuery) },
          "agent",
        );
        return ok(
          `Added ${res.added} opening(s) from ${res.portal}. ${res.note}`,
        );
      } catch (err) {
        return fail(err instanceof Error ? err.message : "Discovery failed.");
      }
    },
  });

  // Tool 2 — add a single role to the board.
  mc.registerTool({
    name: "add_job_to_board",
    description:
      "Add one specific job opening to the user's pipeline board (Discovered " +
      "column). Use when the user or agent has a concrete role to track.",
    inputSchema: {
      type: "object",
      properties: {
        title: { type: "string" },
        company: { type: "string" },
        officialUrl: { type: "string", description: "Direct official posting URL" },
        location: { type: "string" },
        description: { type: "string" },
      },
      required: ["title", "company", "officialUrl"],
    },
    execute(args) {
      const card = actions.addJob(
        {
          title: str(args.title),
          company: str(args.company),
          officialUrl: str(args.officialUrl),
          location: args.location ? str(args.location) : undefined,
          description: args.description ? str(args.description) : undefined,
        },
        "agent",
      );
      return ok(`Added "${card.title}" (id: ${card.id}) to the board.`);
    },
  });

  // Tool 3 — score a board job against the resume and propose tailored bullets.
  mc.registerTool({
    name: "match_with_resume",
    description:
      "Score how well the user's resume fits a job already on the board and " +
      "propose tailored resume bullet improvements. Pass the job's id. Omit " +
      "resumeText to use the resume currently in the workspace.",
    inputSchema: {
      type: "object",
      properties: {
        jobId: {
          type: "string",
          description: "id of a job on the board (from add/discover results)",
        },
        resumeText: {
          type: "string",
          description: "Optional. Defaults to the workspace resume.",
        },
      },
      required: ["jobId"],
    },
    async execute(args) {
      try {
        const match = await actions.match(
          {
            jobId: str(args.jobId),
            resumeText: args.resumeText ? str(args.resumeText) : undefined,
          },
          "agent",
        );
        const diffs = match.bulletDiffs
          .map((d, i) => `  ${i + 1}. ${d.suggested}  (${d.reason})`)
          .join("\n");
        return ok(
          `Fit: ${match.score}%. ${match.summary}\n` +
            `Strengths: ${match.strengths.join(", ") || "—"}\n` +
            `Gaps: ${match.gaps.join(", ") || "—"}\n` +
            `Suggested bullets:\n${diffs || "  (none)"}`,
        );
      } catch (err) {
        return fail(err instanceof Error ? err.message : "Match failed.");
      }
    },
  });

  // Tool 4 (bonus) — let the agent read the current board so it can reason
  // about what to match next. Read-only.
  mc.registerTool({
    name: "list_board_jobs",
    description:
      "List every job currently on the pipeline board with its id, stage, and " +
      "match score. Use to find a jobId before calling match_with_resume.",
    inputSchema: { type: "object", properties: {} },
    execute() {
      const jobs = getState().jobs;
      if (!jobs.length) return ok("The board is empty.");
      const lines = jobs.map(
        (j) =>
          `- ${j.id} · "${j.title}" @ ${j.company} · ${j.stage}` +
          (j.match ? ` · ${j.match.score}% fit` : ""),
      );
      return ok(lines.join("\n"));
    },
  });

  actions.noteSystem(
    `WebMCP connected — ${REGISTERED_TOOL_NAMES.length} tools registered for ` +
      "the in-browser agent.",
    "ok",
  );
  return true;
}
